/*
 * Cart guardrails for Build-a-Box + subscriptions
 * ------------------------------------------------------------------
 * 1. Box grouping: line items sharing a `_box_id` render as one locked
 *    "Your Box" block — per-item quantity steppers and remove links hidden,
 *    replaced with a single box-level "Remove box" action. Stops a customer
 *    pulling one item out and keeping the box (25%) discount below its
 *    minimum (the min is only enforced in the builder, not by the plan).
 * 2. Subscription qty lock: individual (non-box) subscription lines have
 *    their quantity locked — the supply tier maps to a specific quantity, so
 *    changing it in the cart would break the tier <-> price relationship.
 *    Remove stays available.
 *
 * Re-applies on every cart re-render. The theme replaces the items holder's
 * innerHTML on each update (build()), so injected markup is wiped and this
 * simply re-runs on `theme:cart:added` / `theme:cart:loaded`.
 */
(function () {
  'use strict';

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Shopify money formatter (mirrors section-build-a-box.js) using the shop's
  // format from window.theme.moneyFormat.
  function formatMoney(cents, format) {
    var fmt = format || '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    function fmtNum(p, t, d) {
      p = isNaN(p) ? 2 : p;
      t = t === undefined ? ',' : t;
      d = d === undefined ? '.' : d;
      var num = (parseFloat(cents) / 100).toFixed(p);
      var parts = num.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + t);
      return dollars + (parts[1] ? d + parts[1] : '');
    }
    var value;
    switch ((fmt.match(placeholder) || [null, 'amount'])[1]) {
      case 'amount_no_decimals': value = fmtNum(0); break;
      case 'amount_with_comma_separator': value = fmtNum(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmtNum(0, '.', ','); break;
      case 'amount_with_space_separator': value = fmtNum(2, ' ', '.'); break;
      case 'amount_no_decimals_with_space_separator': value = fmtNum(0, ' ', ''); break;
      default: value = fmtNum(2);
    }
    return fmt.replace(placeholder, value);
  }

  function money(cents) {
    return formatMoney(cents, (window.theme && window.theme.moneyFormat) || '${{amount}}');
  }

  function groupTotal(items) {
    return items.reduce(function (s, it) {
      return s + (parseInt(it.getAttribute('data-line-total'), 10) || 0);
    }, 0);
  }

  function groupHolder(holder) {
    var items = holder.querySelectorAll('.cart__item[data-box-id]');
    if (!items.length) return;

    // Bucket items by box id, preserving DOM order.
    var order = [];
    var boxes = {};
    Array.prototype.forEach.call(items, function (item) {
      var id = item.getAttribute('data-box-id');
      if (!boxes[id]) { boxes[id] = []; order.push(id); }
      boxes[id].push(item);
    });

    order.forEach(function (id) {
      var group = boxes[id];
      var first = group[0];
      var count = group.length;

      // Lock each line: mark for CSS + make the quantity field read-only.
      group.forEach(function (item, i) {
        item.classList.add('cart__item--box');
        item.classList.toggle('cart__item--box-first', i === 0);
        item.classList.toggle('cart__item--box-last', i === count - 1);
        var field = item.querySelector('.cart__item__quantity-field');
        if (field) { field.readOnly = true; field.tabIndex = -1; }
      });

      // Skip if this box's header is already in place (run() can fire twice
      // between re-renders — the innerHTML swap is the only thing that removes it).
      var prev = first.previousElementSibling;
      if (prev && prev.classList.contains('bab-box__head') && prev.getAttribute('data-box-head') === id) {
        return;
      }

      var name = first.getAttribute('data-box-name') || 'Your Box';
      var plan = first.getAttribute('data-box-plan') || '';
      var planLabel = plan === 'subscribe' ? ' &middot; Subscription' : (plan === 'once' ? ' &middot; One-time' : '');

      var head = document.createElement('div');
      head.className = 'bab-box__head';
      head.setAttribute('data-box-head', id);
      head.innerHTML =
        '<div class="bab-box__title">' +
          '<span class="bab-box__name">' + escapeHtml(name) + '</span>' +
          '<span class="bab-box__meta">' + count + ' item' + (count === 1 ? '' : 's') + planLabel + '</span>' +
        '</div>' +
        '<div class="bab-box__actions">' +
          '<span class="bab-box__total">' + money(groupTotal(group)) + '</span>' +
          '<button type="button" class="bab-box__remove" data-box-remove>Remove box</button>' +
        '</div>';

      first.parentNode.insertBefore(head, first);
      head.querySelector('[data-box-remove]').addEventListener('click', function () {
        removeBox(group, head);
      });
    });
  }

  function removeBox(group, head) {
    if (head) head.classList.add('is-loading');
    group.forEach(function (i) { i.classList.add('is-removing'); });

    var keys = group.map(function (i) { return i.getAttribute('data-item'); }).filter(Boolean);
    if (!keys.length) return;

    var lastKey = keys[keys.length - 1];
    var lastItem = group[group.length - 1];
    var rest = keys.slice(0, -1);

    // Remove the last line through the theme's own event so it does its normal
    // re-render (no page reload). Any earlier lines are removed silently first.
    function fireThemeRemove() {
      lastItem.dispatchEvent(new CustomEvent('theme:cart:update', {
        bubbles: true,
        detail: { id: lastKey, quantity: 0 }
      }));
    }

    if (!rest.length) { fireThemeRemove(); return; }

    var updates = {};
    rest.forEach(function (k) { updates[k] = 0; });

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates })
    })
      .then(function () { fireThemeRemove(); })
      .catch(function () { window.location.href = '/cart'; });
  }

  // Give each individual (non-box) subscription line the same header-card
  // treatment as a box: a "Subscription · <plan>" header with a Remove button,
  // the quantity locked, and the native per-item controls hidden. The supply
  // tier maps to a specific quantity, so changing qty in the cart would break
  // the tier <-> price relationship.
  function decorateSubscriptions(holder) {
    var subs = holder.querySelectorAll('.cart__item[data-subscription]:not([data-box-id])');
    Array.prototype.forEach.call(subs, function (item) {
      // Reuse the box card look: side/bottom borders + hidden native controls.
      item.classList.add('cart__item--box', 'cart__item--box-first', 'cart__item--box-last');
      var field = item.querySelector('.cart__item__quantity-field');
      if (field) { field.readOnly = true; field.tabIndex = -1; }

      var key = item.getAttribute('data-item');
      var prev = item.previousElementSibling;
      if (prev && prev.classList.contains('bab-box__head') && prev.getAttribute('data-sub-head') === key) {
        return;
      }

      var planName = item.getAttribute('data-plan-name') || 'Subscription';
      // Title matches the subscription window, e.g. "12 Week Supply" / "4 Week Supply".
      var wk = (planName.match(/(\d+)\s*week/i) || [])[1];
      var title = wk ? (wk + ' Week Supply') : planName;
      var head = document.createElement('div');
      head.className = 'bab-box__head';
      head.setAttribute('data-sub-head', key);
      head.innerHTML =
        '<div class="bab-box__title">' +
          '<span class="bab-box__name">' + escapeHtml(title) + '</span>' +
          '<span class="bab-box__meta">Subscription</span>' +
        '</div>' +
        '<div class="bab-box__actions">' +
          '<span class="bab-box__total">' + money(groupTotal([item])) + '</span>' +
          '<button type="button" class="bab-box__remove" data-box-remove>Remove</button>' +
        '</div>';

      item.parentNode.insertBefore(head, item);
      head.querySelector('[data-box-remove]').addEventListener('click', function () {
        removeBox([item], head);
      });
    });
  }

  function run() {
    document.querySelectorAll('[data-items-holder]').forEach(function (holder) {
      groupHolder(holder);
      decorateSubscriptions(holder);
    });
  }

  document.addEventListener('theme:cart:added', run);
  document.addEventListener('theme:cart:loaded', run);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
